/**
 * Wires `location.model.ts` to `PUT /works/:id/location`.
 *
 * Holds the one thing the model cannot: the current location as this screen
 * last saw it, the form's open/draft/saving/saved/error, and the write. After
 * a save the row the server returned becomes `current` — there is no re-read,
 * because the response IS the row — and `onRecorded` hands it up so the
 * record's identity block says the same thing.
 */

import { useCallback, useState } from "react";
import type { WorkLocation, WorkState } from "@ondo/shared";

import { setWorkLocation } from "../lib/api.ts";
import { describeApiError } from "../lib/apiError.ts";
import { LOCATION_COPY } from "./location.constants.ts";
import {
  currentOf,
  describeDraftFault,
  draftFrom,
  toLocationView,
  toRequest,
} from "./location.model.ts";
import type { LocationCurrent, LocationDraft, LocationView } from "./location.model.ts";

export interface LocationController {
  view: LocationView;
  form: {
    open: boolean;
    draft: LocationDraft;
    saving: boolean;
    /** True after a save until the form is opened again. */
    saved: boolean;
  };
  /** Opens on the recorded values, or on the default. */
  openForm: () => void;
  closeForm: () => void;
  setDraft: (patch: Partial<LocationDraft>) => void;
  save: () => void;
  error: string | null;
}

export function useLocationController(input: {
  workId: string;
  /** The record's `work.state`, as loaded. Re-seeds `current` when the record reloads. */
  initial: WorkState | null;
  canAct: boolean;
  onRecorded?: ((location: WorkLocation) => void) | undefined;
}): LocationController {
  const { workId, initial, canAct, onRecorded } = input;

  /**
   * What was saved here, and which record it was saved AGAINST.
   *
   * Derived rather than synced: `current` is the saved row while the record
   * this panel was given is the one the save happened on, and the record's
   * own state the moment the record reloads — a new `initial` is a new truth
   * and the override falls away by comparison, with no effect writing state.
   */
  const [saved, setSaved] = useState<{ of: WorkState | null; row: LocationCurrent } | null>(null);
  const current = saved !== null && saved.of === initial ? saved.row : currentOf(initial);
  const [open, setOpen] = useState(false);
  const [draft, setDraftState] = useState<LocationDraft>(() => draftFrom(currentOf(initial)));
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setDraft = useCallback((patch: Partial<LocationDraft>) => {
    setDraftState((now) => ({ ...now, ...patch }));
    setError(null);
  }, []);

  return {
    view: toLocationView(current, canAct),
    form: { open, draft, saving, saved: justSaved },
    error,

    openForm: () => {
      setDraftState(draftFrom(current));
      setOpen(true);
      setJustSaved(false);
      setError(null);
    },
    closeForm: () => {
      setOpen(false);
      setError(null);
    },
    setDraft,

    save: () => {
      // The one rule the form can answer itself. Asked before the request
      // rather than after a 422, and it is the model's answer rather than a
      // condition here — see `describeDraftFault`.
      const fault = describeDraftFault(draft);
      if (fault !== null) {
        setError(fault);
        return;
      }
      setSaving(true);
      setError(null);
      setWorkLocation(workId, toRequest(draft))
        .then((row) => {
          setSaved({
            of: initial,
            row: {
              kind: row.kind,
              placeText: row.placeText,
              // The row the server wrote, not the draft that asked for it: the
              // country it stored is the one the record now holds.
              countryCode: row.countryCode,
              city: row.city,
            },
          });
          setOpen(false);
          setJustSaved(true);
          onRecorded?.(row);
        })
        .catch((cause: unknown) => {
          setError(describeApiError(cause, LOCATION_COPY.unknownError));
        })
        .finally(() => {
          setSaving(false);
        });
    },
  };
}
